import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Search, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Clock,
  User,
  Mail,
  Calendar,
  Phone,
  MapPin,
  Loader2,
  X,
  Save,
  AlertCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { userApi } from '../../services/admin/userApi';
import roleApi from '../../services/admin/roleApi';

const UserApproval = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [approvalData, setApprovalData] = useState({
    status: 'approved',
    roleId: null
  });
  const [processingApproval, setProcessingApproval] = useState(false);
  const [showEditRole, setShowEditRole] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editRoleData, setEditRoleData] = useState({
    roleId: 2
  });
  const [processingEditRole, setProcessingEditRole] = useState(false);
  const [roles, setRoles] = useState([]);

  // Fetch pending users
  const fetchPendingUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('admin/accounts/pending');
      
      if (response.data.success) {
        setPendingUsers(response.data.data);
      } else {
        toast.error('Có lỗi xảy ra khi tải danh sách users chờ phê duyệt');
      }
    } catch (error) {
      console.error('Error fetching pending users:', error);
      if (error.response?.status === 401) {
        toast.error('Phiên đăng nhập đã hết hạn');
      } else if (error.response?.status === 403) {
        toast.error('Bạn không có quyền truy cập');
      } else {
        toast.error('Có lỗi xảy ra khi tải danh sách users chờ phê duyệt');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch user details
  const fetchUserDetails = async (userId) => {
    try {
      const response = await api.get(`/admin/accounts/${userId}`);
      
      if (response.data.success) {
        setSelectedUser(response.data.data);
        setShowUserDetail(true);
        // Reset approval data
        setApprovalData({
          status: 'approved',
          roleId: null
        });
      } else {
        toast.error('Có lỗi xảy ra khi tải thông tin user');
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error('Có lỗi xảy ra khi tải thông tin user');
    }
  };

  // Handle user approval/rejection
  const handleApproval = async () => {
    if (!selectedUser) return;

    // Validate role selection when approving
    if (approvalData.status === 'approved' && !approvalData.roleId) {
      toast.error('Vui lòng chọn vai trò cho user');
      return;
    }

    setProcessingApproval(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await userApi.approveUser(token, selectedUser.id, approvalData.status, approvalData.roleId);
      
      if (response.data.success) {
        const statusText = approvalData.status === 'approved' ? 'phê duyệt' : 'từ chối';
        toast.success(`Đã ${statusText} user thành công!`);
        
        // Close modal and refresh list
        setShowUserDetail(false);
        setSelectedUser(null);
        fetchPendingUsers();
      } else {
        toast.error('Có lỗi xảy ra khi xử lý phê duyệt');
      }
    } catch (error) {
      console.error('Error processing approval:', error);
      toast.error('Có lỗi xảy ra khi xử lý phê duyệt');
    } finally {
      setProcessingApproval(false);
    }
  };

  // Handle rejection (X button)
  const handleRejection = async () => {
    if (!selectedUser) return;

    setProcessingApproval(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await userApi.approveUser(token, selectedUser.id, 'rejected', null);
      
      if (response.data.success) {
        toast.success('Đã từ chối user thành công!');
        
        // Close modal and refresh list
        setShowUserDetail(false);
        setSelectedUser(null);
        fetchPendingUsers();
      } else {
        toast.error('Có lỗi xảy ra khi từ chối user');
      }
    } catch (error) {
      console.error('Error processing rejection:', error);
      toast.error('Có lỗi xảy ra khi từ chối user');
    } finally {
      setProcessingApproval(false);
    }
  };

  // Close user detail modal
  const closeUserDetail = () => {
    setShowUserDetail(false);
    setSelectedUser(null);
    setApprovalData({
      status: 'approved',
      roleId: null
    });
  };

  // Open edit role modal
  const openEditRole = (user) => {
    setEditingUser(user);
    setEditRoleData({
      roleId: user.role_id || (roles.length > 0 ? roles[0].id : null)
    });
    setShowEditRole(true);
  };

  // Handle edit role
  const handleEditRole = async () => {
    if (!editingUser) return;

    setProcessingEditRole(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await userApi.changeUserRole(token, editingUser.id, editRoleData.roleId);
      
      if (response.data.success) {
        toast.success('Đã cập nhật role thành công! Người dùng sẽ cần đăng nhập lại để áp dụng thay đổi.');
        setShowEditRole(false);
        setEditingUser(null);
        fetchPendingUsers();
      } else {
        toast.error('Có lỗi xảy ra khi cập nhật role');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Có lỗi xảy ra khi cập nhật role');
    } finally {
      setProcessingEditRole(false);
    }
  };

  // Close edit role modal
  const closeEditRole = () => {
    setShowEditRole(false);
    setEditingUser(null);
    setEditRoleData({
      roleId: roles.length > 0 ? roles[0].id : null
    });
  };

  // Initial fetch
  useEffect(() => {
    fetchPendingUsers();
    loadRoles(); // Load roles when component mounts
    console.log('🔍 [FRONTEND] UserApproval component mounted');
  }, []);

  // Load roles from API
  const loadRoles = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await roleApi.getAllRoles(token);
      console.log('🔍 [FRONTEND] loadRoles response:', response);
      
      if (response && response.success) {
        setRoles(response.data || []);
        console.log('✅ [FRONTEND] Roles loaded:', response.data);
      } else {
        console.error('❌ [FRONTEND] API response error:', response);
        throw new Error('API response error');
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Error loading roles:', error);
      setRoles([]);
    }
  };

  const getRoleText = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : 'Chưa phân quyền';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'waiting_approval': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'suspended': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'waiting_approval': return 'Chờ phê duyệt';
      case 'approved': return 'Đã phê duyệt';
      case 'rejected': return 'Từ chối';
      case 'suspended': return 'Tạm khóa';
      default: return status;
    }
  };

  // Debug log for roles state
  console.log('🔍 [FRONTEND] UserApproval render - roles state:', roles);
  console.log('🔍 [FRONTEND] UserApproval render - approvalData:', approvalData);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Phê duyệt User</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Quản lý và phê duyệt tài khoản mới</p>
        </div>
        <div className="flex items-center space-x-2">
          <button 
            onClick={fetchPendingUsers}
            className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Clock className="w-4 h-4" />
            <span>Làm mới</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Tổng chờ phê duyệt</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{pendingUsers.length}</p>
            </div>
            <div className="p-2 sm:p-3 bg-yellow-100 rounded-lg">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
            </div>
          </div>
        </div>
          
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Đã phê duyệt hôm nay</p>
              <p className="text-xl sm:text-2xl font-bold text-green-600">0</p>
            </div>
            <div className="p-2 sm:p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border sm:col-span-2 lg:col-span-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Đã từ chối hôm nay</p>
              <p className="text-xl sm:text-2xl font-bold text-red-600">0</p>
            </div>
            <div className="p-2 sm:p-3 bg-red-100 rounded-lg">
              <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Pending Users List */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-8 sm:py-12">
            <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-blue-600" />
            <span className="ml-2 text-sm sm:text-base text-gray-600">Đang tải danh sách users chờ phê duyệt...</span>
          </div>
        ) : (
          <>
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Danh sách chờ phê duyệt</h3>
            </div>
            
            {/* Mobile Card View */}
            <div className="lg:hidden space-y-3 p-3 sm:p-4">
              {pendingUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="flex flex-col items-center space-y-2">
                    <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-green-400" />
                    <p className="text-base sm:text-lg font-medium">Không có user nào chờ phê duyệt</p>
                    <p className="text-xs sm:text-sm text-gray-400">Tất cả user đã được xử lý</p>
                  </div>
                </div>
              ) : (
                pendingUsers.map((user) => (
                  <div key={user.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {user.name?.charAt(0).toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-xs text-gray-500">ID: {user.id}</div>
                        </div>
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => fetchUserDetails(user.id)}
                          className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                          title="Xem chi tiết và phê duyệt"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => openEditRole(user)}
                          className="text-green-600 hover:text-green-900 p-2 rounded-lg hover:bg-green-50 transition-colors"
                          title="Sửa role"
                        >
                          <Save className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <span className="text-xs text-gray-500">Email</span>
                        <div className="text-xs text-gray-900 mt-1">{user.email}</div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500">Ngày đăng ký</span>
                        <div className="text-xs text-gray-900 mt-1">
                          {new Date(user.created_at).toLocaleDateString('vi-VN')}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(user.status)}`}>
                        {getStatusText(user.status)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ngày đăng ký
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trạng thái
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Thao tác
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pendingUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                            <span className="text-white font-medium text-sm">
                              {user.name?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">ID: {user.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(user.created_at).toLocaleDateString('vi-VN')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(user.status)}`}>
                          {getStatusText(user.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-1 justify-end">
                          <button
                            onClick={() => fetchUserDetails(user.id)}
                            className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                            title="Xem chi tiết và phê duyệt"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          {/* <button
                            onClick={() => openEditRole(user)}
                            className="text-green-600 hover:text-green-900 p-2 rounded-lg hover:bg-green-50 transition-colors"
                            title="Sửa role"
                          >
                            <Save className="w-5 h-5" />
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* User Detail & Approval Modal */}
      {showUserDetail && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Phê duyệt User</h2>
                <button
                  onClick={closeUserDetail}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* User Info */}
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                      <span className="text-white font-bold text-lg sm:text-xl">
                        {selectedUser.name?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">{selectedUser.name}</h3>
                      <p className="text-xs sm:text-sm text-gray-500">{selectedUser.email}</p>
                    </div>
                  </div>

                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-xs sm:text-sm text-gray-600">{selectedUser.email}</span>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-xs sm:text-sm text-gray-600">
                        Đăng ký: {new Date(selectedUser.created_at).toLocaleDateString('vi-VN')}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-xs sm:text-sm text-gray-600">
                        Cập nhật: {new Date(selectedUser.updated_at || selectedUser.created_at).toLocaleDateString('vi-VN')}
                      </span>
                    </div>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="w-5 h-5 text-yellow-600" />
                      <span className="text-sm font-medium text-yellow-800">Chờ phê duyệt</span>
                    </div>
                    <p className="text-sm text-yellow-700 mt-1">
                      User này đang chờ admin phê duyệt hoặc từ chối
                    </p>
                  </div>
                </div>

                {/* Approval Form */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Thông tin phê duyệt</h4>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h5 className="font-medium text-gray-900 mb-3">Chi tiết tài khoản</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">ID:</span>
                        <span className="font-medium">{selectedUser.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email:</span>
                        <span className="font-medium">{selectedUser.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tên:</span>
                        <span className="font-medium">{selectedUser.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Vai trò hiện tại:</span>
                        <span className="font-medium text-yellow-600">
                          {selectedUser.role_name ? getRoleText(selectedUser.role_name) : 'Chưa phân quyền'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Trạng thái:</span>
                        <span className={`font-medium ${getStatusColor(selectedUser.status)}`}>
                          {getStatusText(selectedUser.status)}
                        </span>
                      </div>
                    </div>
                    </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h5 className="font-medium text-gray-900 mb-3">Cài đặt phê duyệt</h5>
                    <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Trạng thái</label>
                        <select
                          value={approvalData.status}
                          onChange={(e) => setApprovalData({...approvalData, status: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="approved">Phê duyệt</option>
                          <option value="rejected">Từ chối</option>
                          <option value="suspended">Tạm khóa</option>
                        </select>
                      </div>
                      
                      {approvalData.status === 'approved' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phân quyền</label>
                          <select
                            value={approvalData.roleId || ''}
                            onChange={(e) => setApprovalData({...approvalData, roleId: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Chọn vai trò</option>
                            {roles.length > 0 ? (
                              roles.map(role => (
                                <option key={role.id} value={role.id}>{role.name}</option>
                              ))
                            ) : (
                              <option value="" disabled>Đang tải vai trò... ({roles.length} roles)</option>
                            )}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={handleRejection}
                  disabled={processingApproval}
                  className="px-3 sm:px-4 py-2 text-sm sm:text-base text-red-700 bg-red-100 rounded-lg hover:bg-red-200 flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4" />
                  <span>Từ chối</span>
                </button>
                <button
                  onClick={closeUserDetail}
                  className="px-3 sm:px-4 py-2 text-sm sm:text-base text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Hủy
                </button>
                <button
                  onClick={handleApproval}
                  disabled={processingApproval}
                  className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {processingApproval ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>{processingApproval ? 'Đang xử lý...' : 'Lưu quyết định'}</span>
                </button>
              </div>
            </div>
          </div>
                 </div>
       )}

       {/* Edit Role Modal */}
       {showEditRole && editingUser && (
         <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
           <div className="bg-white rounded-lg w-full max-w-md max-h-[95vh] overflow-y-auto">
             <div className="p-4 sm:p-6">
               <div className="flex items-center justify-between mb-4 sm:mb-6">
                 <h2 className="text-lg sm:text-xl font-bold text-gray-900">Sửa Role User</h2>
                 <button
                   onClick={closeEditRole}
                   className="text-gray-400 hover:text-gray-600 p-1"
                 >
                   <X className="w-5 h-5 sm:w-6 sm:h-6" />
                 </button>
               </div>

               <div className="space-y-4">
                 {/* User Info */}
                 <div className="flex items-center space-x-3 sm:space-x-4">
                   <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                     <span className="text-white font-bold text-lg sm:text-xl">
                       {editingUser.name?.charAt(0).toUpperCase() || 'U'}
                     </span>
                   </div>
                   <div>
                     <h3 className="text-base sm:text-lg font-semibold text-gray-900">{editingUser.name}</h3>
                     <p className="text-xs sm:text-sm text-gray-500">{editingUser.email}</p>
                   </div>
                 </div>

                 <div className="bg-gray-50 p-4 rounded-lg">
                   <h5 className="font-medium text-gray-900 mb-3">Thông tin hiện tại</h5>
                   <div className="space-y-2 text-sm">
                     <div className="flex justify-between">
                       <span className="text-gray-600">ID:</span>
                       <span className="font-medium">{editingUser.id}</span>
                     </div>
                     <div className="flex justify-between">
                       <span className="text-gray-600">Email:</span>
                       <span className="font-medium">{editingUser.email}</span>
                     </div>
                     <div className="flex justify-between">
                       <span className="text-gray-600">Role hiện tại:</span>
                       <span className="font-medium text-blue-600">
                         {getRoleText(editingUser.role_id)}
                       </span>
                     </div>
                   </div>
                 </div>

                 <div className="bg-blue-50 p-4 rounded-lg">
                   <h5 className="font-medium text-gray-900 mb-3">Chọn Role mới</h5>
                   <div className="space-y-3">
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                       <select
                         value={editRoleData.roleId}
                         onChange={(e) => setEditRoleData({...editRoleData, roleId: parseInt(e.target.value)})}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                       >
                         {roles.map(role => (
                           <option key={role.id} value={role.id}>{role.name}</option>
                         ))}
                       </select>
                     </div>
                   </div>
                 </div>
               </div>

               <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                 <button
                   onClick={closeEditRole}
                   className="px-3 sm:px-4 py-2 text-sm sm:text-base text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                 >
                   Hủy
                 </button>
                 <button
                   onClick={handleEditRole}
                   disabled={processingEditRole}
                   className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center space-x-2 disabled:opacity-50"
                 >
                   {processingEditRole ? (
                     <Loader2 className="w-4 h-4 animate-spin" />
                   ) : (
                     <Save className="w-4 h-4" />
                   )}
                   <span>{processingEditRole ? 'Đang cập nhật...' : 'Cập nhật Role'}</span>
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}

       {/* Edit Role Modal */}
       {showEditRole && editingUser && (
         <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
           <div className="bg-white rounded-lg w-full max-w-md max-h-[95vh] overflow-y-auto">
             <div className="p-4 sm:p-6">
               <div className="flex items-center justify-between mb-4 sm:mb-6">
                 <h2 className="text-lg sm:text-xl font-bold text-gray-900">Sửa Role User</h2>
                 <button
                   onClick={closeEditRole}
                   className="text-gray-400 hover:text-gray-600 p-1"
                 >
                   <X className="w-5 h-5 sm:w-6 sm:h-6" />
                 </button>
               </div>

               <div className="space-y-4">
                 {/* User Info */}
                 <div className="flex items-center space-x-3 sm:space-x-4">
                   <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                     <span className="text-white font-bold text-lg sm:text-xl">
                       {editingUser.name?.charAt(0).toUpperCase() || 'U'}
                     </span>
                   </div>
                   <div>
                     <h3 className="text-base sm:text-lg font-semibold text-gray-900">{editingUser.name}</h3>
                     <p className="text-xs sm:text-sm text-gray-500">{editingUser.email}</p>
                   </div>
                 </div>

                 <div className="bg-gray-50 p-4 rounded-lg">
                   <h5 className="font-medium text-gray-900 mb-3">Thông tin hiện tại</h5>
                   <div className="space-y-2 text-sm">
                     <div className="flex justify-between">
                       <span className="text-gray-600">ID:</span>
                       <span className="font-medium">{editingUser.id}</span>
                     </div>
                     <div className="flex justify-between">
                       <span className="text-gray-600">Email:</span>
                       <span className="font-medium">{editingUser.email}</span>
                     </div>
                     <div className="flex justify-between">
                       <span className="text-gray-600">Role hiện tại:</span>
                       <span className="font-medium text-blue-600">
                         {getRoleText(editingUser.role_id)}
                       </span>
                     </div>
                   </div>
                 </div>

                 <div className="bg-blue-50 p-4 rounded-lg">
                   <h5 className="font-medium text-gray-900 mb-3">Chọn Role mới</h5>
                   <div className="space-y-3">
                     <div>
                       <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                       <select
                         value={editRoleData.roleId}
                         onChange={(e) => setEditRoleData({...editRoleData, roleId: parseInt(e.target.value)})}
                         className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                       >
                         {roles.map(role => (
                           <option key={role.id} value={role.id}>{role.name}</option>
                         ))}
                       </select>
                     </div>
                   </div>
                 </div>
               </div>

               <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                 <button
                   onClick={closeEditRole}
                   className="px-3 sm:px-4 py-2 text-sm sm:text-base text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                 >
                   Hủy
                 </button>
                 <button
                   onClick={handleEditRole}
                   disabled={processingEditRole}
                   className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center space-x-2 disabled:opacity-50"
                 >
                   {processingEditRole ? (
                     <Loader2 className="w-4 h-4 animate-spin" />
                   ) : (
                     <Save className="w-4 h-4" />
                   )}
                   <span>{processingEditRole ? 'Đang cập nhật...' : 'Cập nhật Role'}</span>
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };

export default UserApproval; 
